import React from "react";
import { Box, FormControl, Grid, Typography } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { getAuthenticationRequestSchema, type AuthenticationRequestData } from "../../../schemas";
import { ApplicationWordArt, AuthenticationButton, StyledFormLabel, StyledTextField } from "../../../components";

interface LoginFormProps {
	readonly onSubmit: (data: AuthenticationRequestData) => void;
	readonly defaultUsername: string;
	readonly isSubmitting: boolean;
	readonly isSsoError: boolean;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSubmit, isSubmitting, isSsoError, defaultUsername }) => {
	const intl = useIntl();

	const {
		register,
		handleSubmit,
		formState: { isSubmitting: formStateIsSubmitting, errors },
	} = useForm<AuthenticationRequestData>({
		resolver: zodResolver(getAuthenticationRequestSchema(intl)),
		defaultValues: {
			username: defaultUsername,
		},
	});

	return (
		<Grid container component="form" onSubmit={handleSubmit(onSubmit)} direction="column" gap={6}>
			<Box sx={{ margin: "auto" }}>
				<ApplicationWordArt width={300} />
			</Box>

			<Grid container direction="column" alignItems="center">
				<Typography variant="h1" color="white">
					<FormattedMessage id="login.welcome-back" defaultMessage="Welcome back" />
				</Typography>
				<Typography color="white">
					<FormattedMessage id="login.sign-in-subtitle" defaultMessage="Sign in to your account to continue" />
				</Typography>
			</Grid>

			<Grid container direction="column" gap={2}>
				<FormControl error={Boolean(errors.username) || isSsoError}>
					<StyledFormLabel color="white">
						<FormattedMessage id="login.email" defaultMessage="Email" />
					</StyledFormLabel>

					<StyledTextField
						placeholder={intl.formatMessage({
							id: "login.email.placeholder",
							defaultMessage: "enter your email",
						})}
						fullWidth
						autoFocus
						{...register("username")}
						error={Boolean(errors.username) || isSsoError}
						helperText={errors.username?.message}
						slotProps={{
							htmlInput: {
								"data-testid": "login__input__username",
							},
						}}
					/>
				</FormControl>

				{Boolean(isSsoError) && (
					<Typography color="error" data-testid="login__text__error">
						<FormattedMessage
							id="login.sso-error"
							defaultMessage="Unable to login using Single Sign-On. Please try again."
						/>
					</Typography>
				)}

				<AuthenticationButton
					variant="contained"
					type="submit"
					fullWidth
					size="large"
					loading={formStateIsSubmitting || isSubmitting}
					data-testid="login__button__submit"
				>
					<FormattedMessage id="login.submit" defaultMessage="Next" />
				</AuthenticationButton>
			</Grid>
		</Grid>
	);
};
